import { Suspense, useEffect, useState } from "react";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { TableFilters, TableFiltersState } from "../../../../common/components/ui/table/TableFilters";
import Loading from "../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { getCompanyId, getPaginationCompanies } from "../../../../common/services/company.api";
import { ICompany } from "../../../../types/company.type";
import ModalEditCompanies from "../modals/ModalEditCompanies";
import { STATUS_LABELS } from "../../../vehicles/components/TableVehicles";

const EMPTY_ACTION = { eventName: '', data: undefined }


const formatPhone = (phone: string) => {
  // Remove all non-numeric characters
  const cleanPhone = (phone || '').replace(/\D/g, '');

  // Return original if no valid numbers
  if (!cleanPhone) return phone;

  // Format for mobile phone (11 digits)
  if (cleanPhone.length === 11) {
    return cleanPhone.replace(
      /^(\d{2})(\d{5})(\d{4})$/,
      '($1) $2-$3'
    );
  }

  // Format for landline (10 digits)
  if (cleanPhone.length === 10) {
    return cleanPhone.replace(
      /^(\d{2})(\d{4})(\d{4})$/,
      '($1) $2-$3'
    );
  }

  // Return original if doesn't match expected formats
  return phone;
};

const formatCNPJ = (cnpj: string) => {
  // Remove all non-numeric characters
  const cleanCNPJ = (cnpj || '').replace(/\D/g, '');

  // Return empty if no valid numbers or not enough digits
  if (!cleanCNPJ || cleanCNPJ.length !== 14) return cnpj;

  // Format as XX.XXX.XXX/YYYY-ZZ
  return cleanCNPJ.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
};


const formartCompanies = (companies: ICompany[]) => {
  return companies.map((company) => ({
    _id: company._id,
    cnpj: formatCNPJ(company.cnpj),
    socialname: company.socialname,
    email: <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline">{company.email}</a>,
    phone: formatPhone(company.phone),
    _original: company,
  }));
}

const headers = {
  _id: 'Id da Empresa',
  cnpj: 'CNPJ',
  socialname: 'Nome da Empresa',
  email: 'E-mail',
  phone: 'Telefone',
}

function TableCompanyManagement() {
  const companyId = getCompanyId()
  const { notifyError } = useToastContext()
  const [page, setPage] = useState<number>(1)
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<ICompany[]>([])

  const [action, setAction] = useState<{ eventName: string, data?: unknown }>(EMPTY_ACTION)
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "socialname,email,cnpj",
    orderBy: "socialname",
  })


  useEffect(() => {
    async function loadCompanies(filters, page) {
      setIsLoading(true)

      const result = await getPaginationCompanies({ ...filters, page }) as unknown as {
        ok: boolean;
        message?: string;
        totalItems: number;
        data: ICompany[];
      };

      if (!result.ok)
        return notifyError(result.message || "Error ao carregar empresas");

      setData({ itemsPerPage: data.itemsPerPage, totalItems: result.totalItems });


      setCompanies(result.data);


      setIsLoading(false)
    }

    loadCompanies(filters, page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyId])

  const actions = [
    {
      label: "Editar",
      // Aqui, "data" contém o item formatado da tabela, incluindo _original
      action: (data) => setAction({ eventName: 'edit', data: data._original }),
    }
  ]

  const isAction = (eventName: string) => action.eventName === eventName

  const cleanActionData = () => setAction(EMPTY_ACTION)

  return (
    <Suspense fallback={<Loading />} >
      <div
        className="table-section"
      >
        <TableFilters
          dashboardHeader="Empresas"
          placeholder="Digite o nome, o e-mail ou o CNPJ."
          statusOptions={STATUS_LABELS}
          newItemLabel="Nova Empresa"
          onNewItemClick={() => setAction({ eventName: 'edit', data: undefined })}
          onSearch={setFilters}
          entity="companies"
          tableData={formartCompanies(companies) as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={formartCompanies(companies) as unknown as Record<string, unknown>[]}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          entity="vehicles"
        />

        {isLoading && <Loading />}

        <TableFooter page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction('edit') && <ModalEditCompanies
          data={(action.data as ICompany)}
          companyId={(action.data as string)}
          onClose={cleanActionData}
          onSave={(company: ICompany) => {
            setCompanies((prev) => {
              const index = prev.findIndex(c => c._id === company._id);
              if (index !== -1) {
                const updatedCompanies = [...prev];
                updatedCompanies[index] = company;
                return updatedCompanies;
              }
              return [...prev, company];
            });
            cleanActionData();
          }}
        />}
      </div>
    </Suspense>
  );
}

export default TableCompanyManagement;
